import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { HERO_ANCHOR_ID } from "./ProductJourney";
import { useRef } from "react";

/**
 * Hero — Rolex-grade restraint.
 * The product owns the stage. Type is small, confident, set to the left.
 * No floating badges, no overlapping cards, no stat strip in the stage.
 */
const HeroV2 = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const railFill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-b from-cream via-background to-background pt-16 pb-12 md:pt-20 md:pb-16 lg:min-h-[92vh] lg:flex lg:items-center"
    >
      {/* Ambient glows — restored village warmth */}
      <div className="absolute -top-20 -left-10 w-72 h-72 bg-terracotta/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-golden/15 rounded-full blur-3xl pointer-events-none" />

      {/* Soft radial stage light, centered behind the product */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 60% at 70% 52%, hsl(var(--golden) / 0.10), transparent 70%)",
        }}
      />

      {/* Faint warm sunburst rays behind the product */}
      <svg
        aria-hidden="true"
        className="absolute top-1/2 right-[8%] -translate-y-1/2 w-[640px] h-[640px] text-golden/[0.06] pointer-events-none hidden md:block"
        viewBox="0 0 200 200"
      >
        <g stroke="currentColor" strokeWidth="0.4" fill="none">
          {Array.from({ length: 36 }).map((_, i) => (
            <line
              key={i}
              x1="100"
              y1="100"
              x2="100"
              y2="0"
              transform={`rotate(${i * 10} 100 100)`}
            />
          ))}
        </g>
      </svg>

      {/* Faint Warli horizon at the very bottom */}
      <svg
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-full h-16 text-earth/[0.07] pointer-events-none"
        viewBox="0 0 400 60"
        preserveAspectRatio="none"
        fill="currentColor"
      >
        <path d="M0 40 Q60 20 120 38 T240 35 T400 40 L400 60 L0 60 Z" />
      </svg>

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-center">
          {/* LEFT — restrained type column. On mobile, sits below the stage. */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-5 order-2 lg:order-1 text-center lg:text-left"
          >
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] text-muted-foreground mb-4 sm:mb-5">
              Handcrafted in Rural India
            </p>

            <h1 className="font-display text-[1.75rem] sm:text-4xl lg:text-[2.6rem] xl:text-[3rem] font-semibold text-foreground leading-[1.1] tracking-tight mb-5 lg:mb-6 max-w-[18ch] mx-auto lg:mx-0">
              Welcome to Gaonova — every piece carries a village's heartbeat.
            </h1>

            <div className="space-y-1 text-[13px] sm:text-sm text-muted-foreground mb-7 lg:mb-8">
              <p>Dokra brass · Bastar</p>
              <p>Lost-wax cast, single piece</p>
              <p>Made by Sushila Devi</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button variant="accent" size="lg" asChild>
                <Link to="/shop">Explore the Craft</Link>
              </Button>
              <Button variant="ghost" size="lg" asChild>
                <Link to="/artisans">Meet the Makers</Link>
              </Button>
            </div>

            {/* Quiet utility links — only show on desktop, below CTAs */}
            <div className="hidden lg:flex items-center gap-5 mt-10 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <Link
                to="/shop"
                className="hover:text-foreground transition-colors border-b border-transparent hover:border-foreground/40 pb-0.5"
              >
                View this piece
              </Link>
              <span className="w-px h-3 bg-border" />
              <Link
                to="/story"
                className="hover:text-foreground transition-colors border-b border-transparent hover:border-foreground/40 pb-0.5"
              >
                The craft, in detail
              </Link>
            </div>
          </motion.div>

          {/* RIGHT — pure product stage. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-7 flex flex-col items-center justify-center order-1 lg:order-2"
          >
            <div className="relative w-[220px] h-[330px] sm:w-[280px] sm:h-[420px] md:w-[320px] md:h-[480px] lg:w-[380px] lg:h-[565px]">
              <div id={HERO_ANCHOR_ID} className="absolute inset-0" aria-hidden="true" />
              <div
                aria-hidden="true"
                className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-[60%] h-5 rounded-[50%] pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at center, hsl(var(--earth-brown) / 0.28), transparent 70%)",
                  filter: "blur(10px)",
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right-edge scroll progress rail */}
      <div
        aria-hidden="true"
        className="absolute right-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-1.5 z-20"
      >
        <div className="relative w-px h-40 bg-border/60 overflow-hidden">
          <motion.div
            className="absolute top-0 left-0 w-px bg-foreground/70"
            style={{ height: railFill }}
          />
        </div>
        <span className="text-[9px] uppercase tracking-[0.3em] text-muted-foreground rotate-180 [writing-mode:vertical-rl]">
          Scroll
        </span>
      </div>
    </section>
  );
};

export default HeroV2;
